import React, { useEffect, useRef, useState, useMemo } from 'react';
import classNames from 'classnames';
import { ReactComponent as UnsupportedSvg } from '@assets/icons/MediaViewer/unsupported.svg';
import { useSearchParams } from 'react-router-dom';
import { VideoJsPlayer } from 'video.js';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import {
  AudioVideoMetadataDto,
  DocumentMetadataDto,
  ImageMetadataDto
} from '@api/Api';
import { checkCanPlaySource } from '@helpers/checkCanPlaySource';
import CollaborateOnAnAssetModal from '@components/Modals/BootcampModal/components/CollaborateOnAnAssetModal/CollaborateOnAnAssetModal';
import ShapesDrawingTools from '@pages/MediaViewer/AssetViewer/ShapesDrawingTools';
import useMediaViewerReviewAssetTour from '@hooks/tour/useMediaViewerReviewAssetTour';
import useMediaViewerCreateTaskFromComment from '@hooks/tour/useMediaViewerCreateTaskFromComment';
import useMediaViewerTextHighlightTour from '@hooks/tour/useMediaViewerTextHighlightTour';
import useMediaViewerReviewAudioVideoTour from '@hooks/tour/useMediaViewerReviewAudioVideoTour';
import { useAssetViewerState } from '@hooks/useAssetViewerState';

type AssetViewerProps = {
  isShowShapeDrawingTools?: boolean;
  setSelectedCollapse?: (collapse: string | null) => void;
};

const PdfCard = React.lazy(
  () => import('@pages/MediaViewer/AssetViewer/PdfCard')
);
const ImageCard = React.lazy(
  () => import('@pages/MediaViewer/AssetViewer/ImageCard')
);
const VideoCard = React.lazy(
  () => import('@pages/MediaViewer/AssetViewer/VideoCard')
);

function AssetViewer({
  isShowShapeDrawingTools = true,
  setSelectedCollapse
}: AssetViewerProps) {
  const { selectedVersion } = useAssetViewerState();
  const canLeaveComment = useAssetMVPermissions('comment');
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const assetType:
    | 'video'
    | 'audio'
    | 'raster_image'
    | 'camera_raw_image'
    | 'vector_image'
    | 'document'
    | 'presentation'
    | 'spreadsheet'
    | undefined = useMemo(() => {
    return selectedVersion?.metadata.type;
  }, [selectedVersion?.id]);
  const isImage =
    assetType === 'raster_image' ||
    assetType === 'camera_raw_image' ||
    assetType === 'vector_image';
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';
  const isVideo = assetType === 'video';
  const isAudio = assetType === 'audio';

  const [isUnsupported, setIsUnsupported] = useState<boolean>(false);
  const [isPreparing, setIsPreparing] = useState<boolean>(false);
  const [isContainerExpand, setIsContainerExpand] = useState<boolean>(false);
  const [isAssetReady, setIsAssetReady] = useState<boolean>(false);
  const [collaborateOnAnAssetModal, setCollaborateOnAnAssetModal] =
    useState<boolean>(false);

  const [searchParams] = useSearchParams();

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  useEffect(() => {
    if (!selectedVersion) return;
    if ((selectedVersion.metadata.type as any) === 'unknown') {
      setIsUnsupported(true);
      return;
    }
    if (isVideo) {
      const metadata = selectedVersion.metadata as AudioVideoMetadataDto;
      const videoProxies = Object.entries(metadata.videoProxies).map(
        (el) => el[1]
      );
      const playableVideoProxies = videoProxies.filter(
        (x) => x.status === 'ready'
      );
      if (checkCanPlaySource(selectedVersion)) {
        playableVideoProxies.push(selectedVersion?.sourceUrl);
      }
      const isAnyPending = videoProxies.some((x) => x.status === 'pending');

      if (!playableVideoProxies.length) {
        if (isAnyPending) {
          setIsPreparing(true);
          return;
        }
        setIsUnsupported(true);
      } else {
        setIsPreparing(false);
        setIsUnsupported(false);
      }
    }
    if (isAudio) {
      const metadata = selectedVersion.metadata as AudioVideoMetadataDto;
      if (metadata.audioProxy.status === 'pending') {
        setIsPreparing(true);
        return;
      }
      if (metadata.audioProxy.status === 'ready') {
        setIsPreparing(false);
        setIsUnsupported(false);
        return;
      }
      setIsUnsupported(true);
    }
    if (isDocument || isImage) {
      const metadata = selectedVersion.metadata as
        | ImageMetadataDto
        | DocumentMetadataDto;
      if (metadata.proxy.status === 'pending') setIsPreparing(true);
      else if (metadata.proxy.status === 'ready') {
        setIsPreparing(false);
        setIsUnsupported(false);
      } else setIsUnsupported(true);
    }
  }, [selectedVersion?.metadata, selectedVersion?.id]);

  useEffect(() => {
    const startTour = searchParamsRef.current.get('startTour') ?? undefined;
    if (!asset?.workspaceId) return;
    if (!startTour) return;
    if (isAudio || isUnsupported) setCollaborateOnAnAssetModal(true);
  }, [asset?.workspaceId, isAudio, isUnsupported]);

  useMediaViewerReviewAssetTour({
    isAssetReady,
    isAudio,
    isPreparing,
    isUnsupported,
    assetWorkspaceId: asset?.workspaceId,
    setSelectedCollapse
  });

  useMediaViewerCreateTaskFromComment({
    isAssetReady,
    isPreparing,
    isUnsupported,
    setSelectedCollapse
  });

  useMediaViewerTextHighlightTour({
    isAssetReady,
    isPreparing,
    isUnsupported,
    isDocument,
    setSelectedCollapse
  });

  useMediaViewerReviewAudioVideoTour({
    isAssetReady,
    isPreparing,
    isUnsupported,
    setSelectedCollapse,
    isAudio: isVideo || isAudio
  });

  const playerRef = useRef<VideoJsPlayer>(null);
  const handleAssetReady = () => {
    setIsAssetReady(true);
  };
  const handleAssetError = () => {
    setIsUnsupported(true);
  };

  if (isUnsupported) {
    return (
      <>
        <div
          className={classNames(
            isUnsupported && 'media_viewer_asset-unsupported',
            'media_viewer_asset'
          )}
        >
          <UnsupportedSvg className="b-icon-unsupported" />
          Preview unsupported
        </div>
        {asset?.workspaceId && (
          <CollaborateOnAnAssetModal
            workspaceId={asset.workspaceId}
            setVisible={setCollaborateOnAnAssetModal}
            visible={collaborateOnAnAssetModal}
          />
        )}
      </>
    );
  }

  return (
    <>
      {canLeaveComment && isShowShapeDrawingTools && (
        <ShapesDrawingTools
          player={playerRef.current}
          className={classNames(
            isVideo && 'media_viewer_video',
            isAudio && 'media_viewer_audio',
            isDocument && 'media_viewer_document',
            isContainerExpand && 'media_viewer_asset-expanded'
          )}
        />
      )}
      <div
        className={classNames(
          isVideo && 'media_viewer_video',
          isAudio && 'media_viewer_audio',
          isDocument && 'media_viewer_document',
          isContainerExpand && 'media_viewer_asset-expanded',
          'media_viewer_asset'
        )}
      >
        {(isVideo || isAudio) && selectedVersion && (
          <VideoCard
            ref={playerRef}
            asset={selectedVersion}
            onError={handleAssetError}
            onReady={handleAssetReady}
            isAssetReady={isAssetReady}
            isPreparing={isPreparing}
            setSelectedCollapse={setSelectedCollapse}
          />
        )}
        {isDocument && selectedVersion && (
          <PdfCard
            asset={selectedVersion}
            onReady={handleAssetReady}
            isExpand={isContainerExpand}
            setIsExpand={setIsContainerExpand}
            isPreparing={isPreparing}
            isAssetReady={isAssetReady}
          />
        )}
        {isImage && selectedVersion && (
          <ImageCard
            asset={selectedVersion}
            onReady={handleAssetReady}
            isExpand={isContainerExpand}
            setIsExpand={setIsContainerExpand}
            isPreparing={isPreparing}
            isAssetReady={isAssetReady}
          />
        )}
      </div>
    </>
  );
}

export default AssetViewer;
